import type { MediumArea, MinimumArea as MinimumAreaFacet } from '~/features/facets/types'
import { isEmpty } from '~/lib/empty'
import { useIsPC } from '~/lib/ua'
import { css } from '~/styled-system/css'
import { Links } from './Links'
import { Main } from './Main'
import { PrefectureTab } from './PrefectureTab'
import { Section, TitleLink } from './Section'
import { useSelectedArea, useSelectedAreaMain, useSetSelectedArea } from './area'
import type { Item } from './data/type'

export function Areas() {
  const selectedArea = useSelectedAreaMain()
  const isPc = useIsPC()
  if (isEmpty(selectedArea)) {
    return null
  }
  const title =
    import.meta.env.VITE_MODE === 'ppg' ? (
      '人気エリアから探す'
    ) : (
      <TitleLink title="人気エリアから探す" href="/area/" />
    )
  if (isPc) {
    return (
      <Section
        title={title}
        hiddenContent={<SubContents />}
        titleSideContent={
          <PrefectureTab useSelected={useSelectedArea} useSet={useSetSelectedArea} />
        }
      >
        <Main key={selectedArea.code} type="areas" items={selectedArea.items} />
      </Section>
    )
  }
  return (
    <Section title={title} hiddenContent={<SubContents />}>
      <PrefectureTab useSelected={useSelectedArea} useSet={useSetSelectedArea} />
      <Main key={selectedArea.code} type="areas" items={selectedArea.items} />
    </Section>
  )
}

function SubContents() {
  const selectedArea = useSelectedAreaMain()
  if (isEmpty(selectedArea)) {
    return null
  }
  return (
    <>
      <h2 className={css({ fontSize: 'md', fontWeight: 'bold', color: 'gray.700' })}>
        <TitleLink title={selectedArea.label} href={`/area/${selectedArea.path}`} color="link" />
      </h2>
      <div
        className={css({
          _sd: {
            spaceY: '4',
          },
          _pc: {
            display: 'grid',
            gridTemplateColumns: '2',
            gridColumnGap: '10',
            gridRowGap: '4',
          },
        })}
      >
        <SmallArea area={selectedArea.children} path={selectedArea.path} />
      </div>
    </>
  )
}

function SmallArea({ area, path }: { area: MediumArea; path: string }) {
  return area.smalls.map((small) => (
    <div key={small.code} className={css({ spaceY: '2' })}>
      <h3 className={css({ fontSize: 'sm', fontWeight: 'bold', color: 'gray.700' })}>
        <TitleLink title={small.name} href={`/area/${path}/${small.code}`} color="link" />
      </h3>
      <MinimumArea minimums={small.minimums} code={small.code} path={path} />
    </div>
  ))
}

function MinimumArea({
  code,
  path,
  minimums,
}: { minimums: readonly MinimumAreaFacet[]; code: string; path: string }) {
  if (minimums.length === 0) {
    return null
  }
  return <Links items={toItems(minimums, code, path)} />
}

function toItems(area: readonly MinimumAreaFacet[], code: string, path: string): Item[] {
  return area.map((a) => ({
    label: a.name,
    href: `/area/${path}/${code}/${a.code}`,
    src: '',
  }))
}
